import "../Css/Experience.css";
import React, { useState } from "react";
import { getLogo, getOrganization } from "../AppMethods";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";


const experiences = [
    {
        title: "Product Engineer",
        company: "Doctoranytime, Thessaloniki",
        duration: "09/2026 - Current",
        responsibilities: [
            "Building AI-powered solutions that save doctors time in their daily work.",
            "Turning customer problems into product decisions, from idea to delivery.",
            "Shaping requirements with a focus on usability and business impact.",
            "Bringing a quality-first mindset into product development."
        ],
    },
    {
        title: "Master Thesis | QA Lead",
        company: "Doctoranytime, Thessaloniki",
        duration: "07/2026 - Current",
        responsibilities: [
            "Turned Jira tickets into reviewed and tested Pull Requests using AI agents.",
            "Removed guesswork on unclear tickets by asking questions on Jira automatically.",
            "Made code search free of API costs by running RAG locally.",
            "Let teams start runs and follow live logs from a web app.",
            "Evaluated quality, cost, and reliability against manual QA, automation, and other tools."
        ],
        technologies: [
            {
                title: "QA Workflow",
                stack: ["Python", "LangGraph", "LangChain", "Claude API", "Pydantic"]
            },
            {
                title: "Tools & Integrations",
                stack: ["MCPs", "RAG (ChromaDB)", "Jira", "Git Repos (GitHub, Bitbucket, GitLab)"]
            },
            {
                title: "Control Plane",
                stack: ["Node.js", "Express", "TypeScript", "React.js", "Vite"]
            },
            {
                title: "Both Projects",
                stack: ["Git"]
            }
        ]
    },
    {
        title: "QA Lead",
        company: "Doctoranytime, Thessaloniki",
        duration: "10/2024 - 09/2026",
        responsibilities: [
            "Brought mobile apps under test by building a Maestro project from scratch.",
            "Scaled the QA Tool into a cloud platform with scheduled runs, live reports, and AI failure analysis.",
            "Saved manual effort by automating release notes and hands-on QA processes (LLM-powered agents).",
            "Sped up delivery with an agentic workflow turning Jira tickets into Pull Requests.",
            "Grew the team's automation skills by training manual testers in BugBug, Maestro, and AI tools."
        ],
        technologies: [
            {
                title: "Processes",
                stack: ["Testing Pandas", "Confluence"]
            },
            {
                title: "AI & Automation",
                stack: ["Claude Code", "Cursor", "LangGraph", "LangChain", "RAG (ChromaDB)"]
            },
            {
                title: "LLMs",
                stack: ["OpenAI", "Anthropic"]
            },
            {
                title: "MCPs",
                stack: ["Atlassian", "GitHub", "Git", "Maestro", "Selenium", "Playwright"]
            },
            {
                title: "Testing",
                stack: ["Maestro", "Selenium", "Playwright", "BugBug", "Android Emulator"]
            },
            {
                title: "QA Tool",
                stack: ["Node.js", "Express", "MongoDB", "TypeScript", "React.js", "Vite", "Ant Design", "Docker", "Kubernetes"]
            },
        ]
    },
    {
        title: "Corporal (Special Forces)",
        company: "Hellenic Army / Special Forces, Naousa",
        duration: "01/2024 - 09/2024",
        description: "Served conscription (mandatory draft) under the 2nd Special Forces (Raiders) Battalion.",
        responsibilities: [
            "Finished the training school among the 3 top performers. Received respective honorary badge.",
            "Assigned as trainer for the two following drafts.",
            "Trained in specific military school, ranked 12th out of 24, and promoted from Private to Corporal.",
            "Later, assigned managing obligations of the company office.",
        ],
    },
    {
        title: "QA Lead",
        company: "Doctoranytime, Thessaloniki",
        duration: "03/2023 - 01/2024",
        responsibilities: [
            "Cut testing time and improved quality by streamlining QA processes.",
            "Reduced manual effort by automating processes through the QA Tool.",
            "Lowered run and maintenance time by restructuring the test suite.",
            "Expanded test coverage across manual and automated testing."
        ],
        technologies: [
            {
                title: "Processes",
                stack: ["Jira", "Jira REST API", "Google Sheets", "Google Sheets API", "Selenium IDE"]
            }
        ]
    },
    {
        title: "Senior QA Engineer",
        company: "Doctoranytime, Thessaloniki",
        duration: "09/2022 - 02/2023",
        responsibilities: [
            "Shaped the testing roadmap together with the Technical Manager.",
            "Got new QA members productive faster by leading their onboarding.",
            "Increased test coverage with new scenarios and cases.",
            "Improved test execution and reliability by optimizing pipelines with DevOps.",
            "Boosted QA team productivity with new and upgraded QA Tool features."
        ],
        technologies: [
            {
                title: "Communication/Processes",
                stack: ["Jira", "Miro", "Google Sheets", "Google Docs", "Slack"]
            },
        ]
    },
    {
        title: "Master Thesis | QA Engineer",
        company: "Doctoranytime, Thessaloniki",
        duration: "07/2022 - 02/2023",
        responsibilities: [
            "Modernized the existing test framework with newer tools and practices.",
            "Made tests readable for non-technical teams by combining BDD with TDD.",
            "Gave teams flexibility by building a QA Tool supporting both TDD and BDD.",
        ],
        technologies: [
            {
                title: "Testing Project",
                stack: ["Python", "TypeScript/JavaScript", "Playwright", "Cucumber", "Allure"]
            },
            {
                title: "QA Tool",
                stack: ["Node.js", "Express", "React.js", "React-Bootstrap"]
            },
            {
                title: "Both Projects",
                stack: ["Git"]
            }
        ]
    },
    {
        title: "Associate QA Engineer",
        company: "Doctoranytime, Thessaloniki",
        duration: "09/2021 - 08/2022",
        responsibilities: [
            "Set the QA foundation by co-creating the Test Plan and Design.",
            "Caught bugs earlier with TDD-based test scenarios.",
            "Gave the team clear quality visibility through automated reporting.",
            "Sped up feedback by integrating tests into CI/CD with DevOps.",
            "Simplified test management by building an internal QA Tool.",
            "Made setup consistent by Dockerizing both projects."
        ],
        technologies: [
            {
                title: "Testing Project",
                stack: ["Python", "Selenium", "Pytest", "Allure", "Postman"]
            },
            {
                title: "QA Tool",
                stack: ["Node.js", "React.js", "React-Bootstrap"]
            },
            {
                title: "Both Projects",
                stack: ["Docker", "Git", "Bitbucket"]
            }
        ]
    },
    {
        title: "Junior SAP Developer",
        company: "Deloitte, Thessaloniki",
        duration: "12/2020 - 08/2021",
        responsibilities: [
            "Resolved issues during the upgrade from the old SAP system to SAP S/4HANA.",
            "Adjusted and improved several SAP programs.",
        ],
        technologies: [
            {
                title: "All Projects",
                stack: ["ABAP", "SQL", "SAP Netweaver", "SAPUI5"]
            }
        ]
    },
    {
        title: "Bachelor Thesis | Software Engineer",
        company: "Neunen IT, Thessaloniki",
        duration: "08/2020 - 02/2021",
        responsibilities: [
            "Created a monitoring tool with synthetic transaction implementation.",
            "Implemented a suite of automated test cases to validate the monitoring tool's software quality."
        ],
        technologies: [
            {
                title: "Test Project",
                stack: ["Postman", "Java", "Selenium"]
            },
            {
                title: "Monitoring Tool",
                stack: ["React.js", "React-Bootstrap", "JSON", "PHP", "SQL"]
            },
            {
                title: "Both Projects",
                stack: ["Git"]
            }
        ]
    },
];

// "MM/YYYY" -> month index; "Current" -> this month
const parseMonth = (value) => {
    if (value.trim().toLowerCase() === "current") {
        const now = new Date();
        return now.getFullYear() * 12 + now.getMonth();
    }
    const [month, year] = value.trim().split("/").map(Number);
    return year * 12 + (month - 1);
};

const getRange = (duration) => {
    const [start, end] = duration.split(" - ");
    return { start: parseMonth(start), end: parseMonth(end), startLabel: start.trim(), endLabel: end.trim() };
};

// Inclusive month count, formatted like LinkedIn ("2 yrs 1 mo")
const formatLength = (start, end) => {
    const totalMonths = end - start + 1;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts = [];
    if (years) parts.push(`${years} ${years === 1 ? "yr" : "yrs"}`);
    if (months) parts.push(`${months} ${months === 1 ? "mo" : "mos"}`);
    return parts.join(" ");
};

const splitCompany = (company) => {
    const [name, ...location] = company.split(",");
    return { name: name.trim(), location: location.join(",").trim() };
};

// Consecutive roles at the same company are shown as one group
const groupExperiences = (items) => items.reduce((groups, exp, index) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.company === exp.company) {
        lastGroup.roles.push({ ...exp, index });
    } else {
        groups.push({ company: exp.company, roles: [{ ...exp, index }] });
    }
    return groups;
}, []);

const ExperienceDetails = ({ exp }) => (
    <div className="experience-details">

        {exp?.description && <div style={{ marginBottom: "5px" }}>{exp?.description}</div>}

        <ul className="results-list mb-3">
            {exp.responsibilities.map((resp, idx) => (
                <li className={`result ${resp.startsWith("-") ? "without-tick" : "with-tick"}`} key={idx}>
                    {resp}
                </li>
            ))}
        </ul>

        {
            exp?.technologies
            &&
            <div className="technologies">
                <div className="mb-2">🛠️ Technologies Used:</div>
                <ul>
                    {
                        exp.technologies.map((tech, techIdx) => (
                            <li key={techIdx} className="technology-item without-tick">{tech.title} {"->"} {tech.stack.join(", ")}</li>
                        ))
                    }
                </ul>
            </div>
        }

    </div>
);

const ToggleButton = ({ isOpen, onToggle }) => (
    <button
        className="toggle-btn"
        onClick={(e) => {
            e.stopPropagation();
            onToggle();
        }}
        aria-label="Toggle Experience Details"
    >
        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
    </button>
);

const Experience = () => {

    const [activeExperiences, setActiveExperiences] = useState([]);

    const toggleDetails = (index) => {
        setActiveExperiences((prev) => prev.includes(index) ? prev.filter(item => item !== index) : [...prev, index]);
    };

    const isOpen = (index) => activeExperiences.includes(index);

    return (
        <section className="mb-3" id="experience">
            <h2 className="section-title">
                <i className="fas fa-briefcase"></i><span style={{ marginLeft: "10px", textAlign: "center" }}>Experience</span>
            </h2>

            {
                groupExperiences(experiences).map((group) => {

                    const organization = getOrganization(group.company);
                    const { name, location } = splitCompany(group.company);

                    // Single role: compact card
                    if (group.roles.length === 1) {

                        const exp = group.roles[0];
                        const range = getRange(exp.duration);

                        return (
                            <div key={exp.index} className={`work-experience-item ${isOpen(exp.index) ? "open" : ""}`}>

                                <div className="experience-header" onClick={() => toggleDetails(exp.index)}>

                                    <img src={getLogo(organization)} alt="company-logo" className="organization-logo" data-organization={organization} />

                                    <div className="experience-heading">
                                        <h5 className="experience-title">{exp.title}</h5>
                                        <div className="experience-meta">
                                            <span>{exp.company}</span>
                                            <span className="experience-meta-separator">•</span>
                                            <span>{exp.duration}</span>
                                            <span className="experience-meta-separator">•</span>
                                            <span>{formatLength(range.start, range.end)}</span>
                                        </div>
                                    </div>

                                    <ToggleButton isOpen={isOpen(exp.index)} onToggle={() => toggleDetails(exp.index)} />

                                </div>

                                {isOpen(exp.index) && <ExperienceDetails exp={exp} />}

                            </div>
                        );

                    }

                    // Multiple consecutive roles: one company card with a role timeline
                    const ranges = group.roles.map(role => getRange(role.duration));
                    const groupStart = Math.min(...ranges.map(range => range.start));
                    const groupEnd = Math.max(...ranges.map(range => range.end));
                    const startLabel = ranges.find(range => range.start === groupStart).startLabel;
                    const endLabel = ranges.find(range => range.end === groupEnd).endLabel;

                    return (
                        <div key={group.roles[0].index} className="work-experience-item experience-group">

                            <div className="experience-header experience-group-header">

                                <img src={getLogo(organization)} alt="company-logo" className="organization-logo" data-organization={organization} />

                                <div className="experience-heading">
                                    <h5 className="experience-title">{name}</h5>
                                    <div className="experience-meta">
                                        {location && <span>{location}</span>}
                                        {location && <span className="experience-meta-separator">•</span>}
                                        <span>{startLabel} - {endLabel}</span>
                                        <span className="experience-meta-separator">•</span>
                                        <span>{formatLength(groupStart, groupEnd)}</span>
                                    </div>
                                </div>

                                <span className="experience-roles-count">{group.roles.length} roles</span>

                            </div>

                            <ol className="role-timeline">
                                {
                                    group.roles.map((role, roleIdx) => {

                                        const range = ranges[roleIdx];
                                        const isCurrent = range.endLabel.toLowerCase() === "current";

                                        return (
                                            <li key={role.index} className={`role-item ${isCurrent ? "current" : ""} ${isOpen(role.index) ? "open" : ""}`}>

                                                <span className="role-dot" />

                                                <div className="role-header" onClick={() => toggleDetails(role.index)}>

                                                    <div className="role-heading">
                                                        <div className="role-title">{role.title}</div>
                                                        <div className="experience-meta">
                                                            <span>{role.duration}</span>
                                                            <span className="experience-meta-separator">•</span>
                                                            <span>{formatLength(range.start, range.end)}</span>
                                                        </div>
                                                    </div>

                                                    <ToggleButton isOpen={isOpen(role.index)} onToggle={() => toggleDetails(role.index)} />

                                                </div>

                                                {isOpen(role.index) && <ExperienceDetails exp={role} />}

                                            </li>
                                        );

                                    })
                                }
                            </ol>

                        </div>
                    );

                })
            }

        </section>
    );
};

export default Experience;
